import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { PAGE_SEO } from '../seo';

export default function TermsOfService() {
  return (
    <div className="min-h-screen flex flex-col">
      <SEOHead page={PAGE_SEO.terms} />
      <Header />
      <div className="flex-1 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center text-pb-gray-500 hover:text-pb-orange mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="text-3xl font-display font-bold text-pb-gray-800 mb-2">
            Terms of Service
          </h1>
          <p className="text-pb-gray-500">Last updated: February 2025</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-rhythmic-xl p-8 space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-pb-gray-800 mb-4">
              1. Acceptance of Terms
            </h2>
            <p className="text-pb-gray-600 leading-relaxed">
              By installing, accessing, or using PartnerBuddy ("the Service"), you agree
              to be bound by these Terms of Service. If you do not agree to these terms,
              do not use the Service. These terms apply to all users, including workspace
              administrators and end users.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-pb-gray-800 mb-4">
              2. Description of Service
            </h2>
            <div className="space-y-4 text-pb-gray-600 leading-relaxed">
              <p>
                PartnerBuddy is a messaging platform application (available on Slack and Microsoft Teams)
                that enables natural language interaction with AWS Partner Central ACE opportunities. The Service:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Queries and displays AWS ACE opportunity data in Slack</li>
                <li>Integrates with CRM systems (HubSpot, Salesforce) for data synchronization</li>
                <li>Uses artificial intelligence to process natural language queries</li>
                <li>Generates reports and insights on partner opportunities</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-pb-gray-800 mb-4">
              3. AI-Generated Content Disclaimer
            </h2>
            <div className="space-y-4 text-pb-gray-600 leading-relaxed">
              <div className="bg-pb-orange/5 border-l-4 border-pb-orange p-4 rounded">
                <p className="font-semibold text-pb-gray-800">Important Notice</p>
                <p className="text-pb-gray-700 mt-2">
                  PartnerBuddy uses artificial intelligence to generate responses. AI-generated
                  content may contain errors, inaccuracies, or outdated information. You should
                  always verify critical business information through official AWS Partner Central
                  and CRM interfaces.
                </p>
              </div>
              <p>
                We do not guarantee the accuracy, completeness, or reliability of AI-generated
                responses. The Service is provided as a convenience tool and should not be the
                sole source for business-critical decisions.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-pb-gray-800 mb-4">
              4. Subscription and Billing
            </h2>
            <div className="space-y-4 text-pb-gray-600 leading-relaxed">
              <p>
                PartnerBuddy is offered through AWS Marketplace. By subscribing:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>You agree to AWS Marketplace terms and conditions</li>
                <li>Billing is handled through your AWS account</li>
                <li>Subscription fees are charged according to the pricing plan selected</li>
                <li>Cancellation must be done through AWS Marketplace</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-pb-gray-800 mb-4">
              5. User Responsibilities
            </h2>
            <div className="space-y-4 text-pb-gray-600 leading-relaxed">
              <p>As a user of the Service, you agree to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide accurate configuration information</li>
                <li>Maintain the security of your AWS IAM roles and API credentials</li>
                <li>Use the Service only for legitimate business purposes</li>
                <li>Comply with AWS Partner Central terms of service</li>
                <li>Comply with Slack's terms of service and acceptable use policies</li>
                <li>Not attempt to reverse engineer, modify, or misuse the Service</li>
                <li>Not use the Service to transmit harmful, illegal, or inappropriate content</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-pb-gray-800 mb-4">
              6. Acceptable Use
            </h2>
            <div className="space-y-4 text-pb-gray-600 leading-relaxed">
              <p>You may NOT use the Service to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe on intellectual property rights</li>
                <li>Transmit malicious code or attempt to compromise security</li>
                <li>Exceed reasonable usage limits or abuse the API</li>
                <li>Scrape, harvest, or collect data beyond normal Service functionality</li>
                <li>Impersonate other users or organizations</li>
                <li>Interfere with other users' access to the Service</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-pb-gray-800 mb-4">
              7. Data and Security
            </h2>
            <div className="space-y-4 text-pb-gray-600 leading-relaxed">
              <p>
                Your use of the Service is also governed by our{' '}
                <Link to="/privacy" className="text-pb-orange hover:underline">
                  Privacy Policy
                </Link>
                . By using the Service, you consent to our data practices as described
                in the Privacy Policy.
              </p>
              <p>
                You are responsible for maintaining appropriate access controls within
                your Slack workspace. We recommend limiting bot access to specific
                channels containing authorized users only.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-pb-gray-800 mb-4">
              8. Third-Party Services
            </h2>
            <div className="space-y-4 text-pb-gray-600 leading-relaxed">
              <p>
                The Service integrates with third-party services including:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Slack - Subject to Slack's terms of service</li>
                <li>AWS Partner Central - Subject to AWS Partner Program terms</li>
                <li>Amazon Bedrock - Subject to AWS terms of service</li>
                <li>HubSpot/Salesforce - Subject to respective terms of service</li>
              </ul>
              <p className="mt-4">
                We are not responsible for the availability, accuracy, or practices of
                these third-party services.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-pb-gray-800 mb-4">
              9. Limitation of Liability
            </h2>
            <div className="space-y-4 text-pb-gray-600 leading-relaxed">
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>
                  The Service is provided "AS IS" without warranties of any kind
                </li>
                <li>
                  We are not liable for any indirect, incidental, special, consequential,
                  or punitive damages
                </li>
                <li>
                  Our total liability shall not exceed the amount paid for the Service
                  in the 12 months preceding the claim
                </li>
                <li>
                  We are not responsible for losses due to AI inaccuracies, service
                  interruptions, or third-party service failures
                </li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-pb-gray-800 mb-4">
              10. Indemnification
            </h2>
            <p className="text-pb-gray-600 leading-relaxed">
              You agree to indemnify and hold harmless PartnerBuddy, its affiliates,
              and their respective officers, directors, employees, and agents from any
              claims, damages, losses, or expenses arising from your use of the Service
              or violation of these terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-pb-gray-800 mb-4">
              11. Service Modifications and Termination
            </h2>
            <div className="space-y-4 text-pb-gray-600 leading-relaxed">
              <p>We reserve the right to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Modify, suspend, or discontinue the Service at any time</li>
                <li>Update these terms with reasonable notice</li>
                <li>Terminate your access for violation of these terms</li>
                <li>Remove or modify features as needed</li>
              </ul>
              <p className="mt-4">
                You may terminate your use at any time by uninstalling the app from
                your Slack workspace and canceling your AWS Marketplace subscription.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-pb-gray-800 mb-4">
              12. Governing Law
            </h2>
            <p className="text-pb-gray-600 leading-relaxed">
              These terms shall be governed by and construed in accordance with the
              laws of the United States. Any disputes shall be resolved through binding
              arbitration or in the courts of competent jurisdiction.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-pb-gray-800 mb-4">
              13. Contact Information
            </h2>
            <div className="space-y-4 text-pb-gray-600 leading-relaxed">
              <p>
                For questions about these Terms of Service, contact us at:
              </p>
              <div className="bg-pb-gray-50 rounded-lg p-4 mt-4">
                <p><strong>Email:</strong>{' '}
                  <a href="mailto:support@rhythmic.dev" className="text-pb-orange hover:underline">
                    support@rhythmic.dev
                  </a>
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
      </div>
      <Footer />
    </div>
  );
}
